import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { useNavigate } from "react-router-dom";

const API = "https://live-evo.com/api/agencies";

function headers(json = false) {
  return {
    ...(json ? {"Content-Type": "application/json"} : {}),
    Authorization:
      `Bearer ${localStorage.getItem("evoAccessToken") || ""}`
  };
}

async function apiFetch(url, options = {}) {
  let response = await fetch(url, options);

  if (response.status !== 401) {
    return response;
  }

  const token = localStorage.getItem("evoAccessToken");

  if (!token) {
    return response;
  }

  const refresh = await fetch(
    "https://live-evo.com/api/auth/refresh",
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );

  const data = await refresh.json();

  if (!refresh.ok || !data.accessToken) {
    return response;
  }

  localStorage.setItem(
    "evoAccessToken",
    data.accessToken
  );

  return fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      Authorization: `Bearer ${data.accessToken}`
    }
  });
}

function parseList(value) {
  if (Array.isArray(value)) return value;

  try {
    const parsed = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function mediaUrl(value) {
  const url = String(value || "").trim();
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `https://live-evo.com${
    url.startsWith("/") ? url : `/${url}`
  }`;
}

const LABELS = {
  draft: "Brouillon",
  pending: "En attente",
  changes_requested: "Corrections demandées",
  approved: "Validée",
  rejected: "Refusée",
  suspended: "Suspendue",
  revoked: "Retirée"
};

function AdminAgenciesPage() {
  const navigate = useNavigate();
  const [agencies, setAgencies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] =
    useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadAgencies = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const response = await apiFetch(
        `${API}/admin/all`,
        {headers: headers()}
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.error || "Chargement impossible"
        );
      }

      setAgencies(
        Array.isArray(data.agencies)
          ? data.agencies
          : []
      );
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAgencies();
  }, [loadAgencies]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();

    return agencies.filter(agency => {
      const matchesStatus =
        filter === "all" ||
        agency.status === filter;

      const matchesSearch =
        !query ||
        String(agency.name || "")
          .toLowerCase()
          .includes(query) ||
        String(agency.president_pseudo || "")
          .toLowerCase()
          .includes(query);

      return matchesStatus && matchesSearch;
    });
  }, [agencies, filter, search]);

  const counts = useMemo(() => ({
    all: agencies.length,
    approved: agencies.filter(
      item => item.status === "approved"
    ).length,
    suspended: agencies.filter(
      item => item.status === "suspended"
    ).length,
    revoked: agencies.filter(
      item => item.status === "revoked"
    ).length
  }), [agencies]);

  const openAgency = async agency => {
    try {
      setDetailsLoading(true);
      setError("");
      setSuccess("");
      setReason("");

      const response = await apiFetch(
        `${API}/staff/${agency.id}`,
        {headers: headers()}
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.error || "Dossier indisponible"
        );
      }

      setSelected(data);
    } catch (detailsError) {
      setError(detailsError.message);
    } finally {
      setDetailsLoading(false);
    }
  };

  const control = async action => {
    if (
      action !== "reactivate" &&
      reason.trim().length < 10
    ) {
      setError(
        "Écris un motif d’au moins 10 caractères."
      );
      return;
    }

    if (
      action === "revoke" &&
      !window.confirm(
        "Retirer définitivement la certification de cette agence ? Son historique sera conservé."
      )
    ) {
      return;
    }

    try {
      setProcessing(true);
      setError("");
      setSuccess("");

      const response = await apiFetch(
        `${API}/admin/${selected.agency.id}/control`,
        {
          method: "POST",
          headers: headers(true),
          body: JSON.stringify({
            action,
            reason: reason.trim()
          })
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.error || "Action impossible"
        );
      }

      setSuccess(data.message);
      setReason("");
      setSelected(null);
      await loadAgencies();
    } catch (actionError) {
      setError(actionError.message);
    } finally {
      setProcessing(false);
    }
  };

  const requestedLevel = Number(
    selected?.reviews?.find(
      item => item.review_type === "creation"
    )?.requested_level || 0
  );

  return (
    <div className="ag-admin">
      <style>{`
        *{box-sizing:border-box}
        .ag-admin{min-height:100vh;padding:24px 16px 70px;color:#fff;background:radial-gradient(circle at 10% 0%,#073457 0%,#020b14 32%,#00040a 100%)}
        .ag-wrap{width:min(1200px,100%);margin:auto}
        .ag-head{display:flex;align-items:center;gap:15px;margin-bottom:22px}
        .ag-back{width:44px;height:44px;border-radius:50%;border:1px solid #00eaff;background:#051522;color:#fff;font-size:28px;cursor:pointer}
        .ag-eyebrow{color:#00eaff;font-size:10px;font-weight:900;letter-spacing:2px}
        .ag-title{margin:4px 0;font-size:clamp(25px,5vw,38px)}
        .ag-muted{color:#8199ad;font-size:12px}
        .ag-stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:10px;margin:20px 0}
        .ag-stat{padding:15px;border-radius:16px;background:#071725;border:1px solid #173d53}
        .ag-stat b{display:block;color:#00eaff;font-size:25px}
        .ag-toolbar{display:flex;flex-wrap:wrap;gap:10px;margin-bottom:18px}
        .ag-search{flex:1 1 240px;padding:12px 14px;border-radius:14px;border:1px solid #28526b;background:#020810;color:#fff}
        .ag-filter{padding:10px 13px;border-radius:13px;border:1px solid #23465b;background:#07131e;color:#a9bfd0;cursor:pointer}
        .ag-filter.active{border-color:#00eaff;color:#00eaff;background:#073148}
        .ag-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:14px}
        .ag-card{padding:17px;border-radius:20px;border:1px solid rgba(0,234,255,.35);background:linear-gradient(145deg,#071b2c,#030912);color:#fff;text-align:left;cursor:pointer;box-shadow:0 10px 25px rgba(0,0,0,.25)}
        .ag-card:hover{transform:translateY(-2px);border-color:#00eaff}
        .ag-row{display:flex;gap:13px;align-items:center}
        .ag-logo{width:62px;height:62px;flex:0 0 62px;display:grid;place-items:center;overflow:hidden;border-radius:18px;border:1px solid #00eaff;background:#0b2b45;color:#00eaff;font-size:25px;font-weight:900}
        .ag-logo img,.ag-big-logo img{width:100%;height:100%;object-fit:cover}
        .ag-name{font-size:19px;font-weight:900;overflow-wrap:anywhere}
        .ag-status{display:inline-block;margin:4px 0;padding:4px 8px;border-radius:10px;color:#00eaff;background:#073048;font-size:9px;font-weight:900;text-transform:uppercase}
        .ag-meta{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:14px}
        .ag-meta div{padding:9px;border-radius:11px;background:#06131f;color:#a6bbcb;font-size:11px}
        .ag-open{margin-top:13px;text-align:right;color:#00eaff;font-weight:800}
        .ag-empty,.ag-error,.ag-success{padding:16px;border-radius:15px;margin:12px 0}
        .ag-empty{text-align:center;background:#05101a}
        .ag-error{background:#48101d;border:1px solid #ff476a;color:#ffd8df}
        .ag-success{background:#073324;border:1px solid #22df91;color:#bfffe4}
        .ag-overlay{position:fixed;inset:0;z-index:1000;overflow-y:auto;padding:18px;background:rgba(0,0,0,.9)}
        .ag-modal{position:relative;width:min(900px,100%);margin:16px auto;padding:clamp(19px,4vw,32px);border-radius:25px;border:1px solid #00eaff;background:linear-gradient(145deg,#071c2e,#02060c);box-shadow:0 0 35px rgba(0,234,255,.22)}
        .ag-close{position:absolute;right:13px;top:13px;width:40px;height:40px;border-radius:50%;border:1px solid #456;background:#03070b;color:#fff;font-size:24px;cursor:pointer}
        .ag-hero{display:flex;flex-wrap:wrap;align-items:center;gap:17px;padding:17px;margin:14px 0;border-radius:20px;background:linear-gradient(135deg,#08263f,#141039);border:1px solid rgba(0,234,255,.45)}
        .ag-big-logo{width:95px;height:95px;flex:0 0 95px;display:grid;place-items:center;overflow:hidden;border-radius:26px;border:1px solid #00eaff;background:#0b2b45;color:#00eaff;font-size:38px;font-weight:900}
        .ag-info{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:9px}
        .ag-box,.ag-detail{padding:13px;border-radius:14px;background:#061521;border:1px solid #173e54}
        .ag-box{display:flex;flex-direction:column;gap:5px}
        .ag-section{margin:22px 0 9px;font-size:16px}
        .ag-detail{color:#bdd0df;line-height:1.6;white-space:pre-wrap;overflow-wrap:anywhere}
        .ag-tags{display:flex;flex-wrap:wrap;gap:7px}
        .ag-tag{padding:6px 9px;border-radius:11px;background:#073049;color:#bdfaff;font-size:11px}
        .ag-textarea{width:100%;min-height:110px;margin-top:19px;padding:13px;resize:vertical;border-radius:14px;border:1px solid #2c536b;background:#02070c;color:#fff}
        .ag-actions{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:9px;margin-top:13px}
        .ag-action{padding:12px;border-radius:13px;border:1px solid #31536a;background:#0a1823;color:#fff;font-weight:900;cursor:pointer}
        .warn{border-color:#ffbf3f;color:#ffd679}.suspend{border-color:#ff784d;color:#ffae94}.reactivate{border-color:#20dd93;color:#8dffd0}.revoke{border-color:#ff365f;color:#ff8aa2}
        @media(max-width:560px){.ag-admin{padding:16px 10px 55px}.ag-meta{grid-template-columns:1fr}.ag-modal{padding:18px 13px}.ag-big-logo{width:76px;height:76px;flex-basis:76px}}
      `}</style>

      <div className="ag-wrap">
        <div className="ag-head">
          <button
            className="ag-back"
            onClick={() => navigate("/admin")}
          >
            ‹
          </button>
          <div>
            <div className="ag-eyebrow">
              EVO CONTROL CENTER
            </div>
            <h1 className="ag-title">
              Gestion des agences
            </h1>
            <div className="ag-muted">
              Surveillance et décisions réservées à l’Admin
            </div>
          </div>
        </div>

        {error && <div className="ag-error">{error}</div>}
        {success && <div className="ag-success">{success}</div>}

        <div className="ag-stats">
          <div className="ag-stat"><b>{counts.all}</b>Total</div>
          <div className="ag-stat"><b>{counts.approved}</b>Validées</div>
          <div className="ag-stat"><b>{counts.suspended}</b>Suspendues</div>
          <div className="ag-stat"><b>{counts.revoked}</b>Retirées</div>
        </div>

        <div className="ag-toolbar">
          <input
            className="ag-search"
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Rechercher une agence ou un président…"
          />
          {[
            ["all", "Toutes"],
            ["approved", "Validées"],
            ["pending", "En attente"],
            ["suspended", "Suspendues"],
            ["revoked", "Retirées"]
          ].map(item => (
            <button
              key={item[0]}
              className={`ag-filter ${
                filter === item[0] ? "active" : ""
              }`}
              onClick={() => setFilter(item[0])}
            >
              {item[1]}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="ag-empty">Chargement des agences…</div>
        ) : filtered.length === 0 ? (
          <div className="ag-empty">Aucune agence dans cette catégorie.</div>
        ) : (
          <div className="ag-grid">
            {filtered.map(agency => (
              <button
                key={agency.id}
                className="ag-card"
                onClick={() => openAgency(agency)}
              >
                <div className="ag-row">
                  <div className="ag-logo">
                    {agency.logo_url ? (
                      <img
                        src={mediaUrl(agency.logo_url)}
                        alt=""
                      />
                    ) : (
                      String(agency.name || "A")
                        .slice(0, 1)
                        .toUpperCase()
                    )}
                  </div>
                  <div>
                    <div className="ag-name">{agency.name}</div>
                    <div className="ag-status">
                      {LABELS[agency.status] || agency.status}
                    </div>
                    <div className="ag-muted">
                      Président : {agency.president_pseudo || "Utilisateur EVO"}
                    </div>
                  </div>
                </div>

                <div className="ag-meta">
                  <div>Pack : {agency.bonus_level || agency.requested_level || 0} %</div>
                  <div>Membres : {agency.member_count || 0}/{agency.member_limit}</div>
                </div>

                <div className="ag-open">Contrôler l’agence ›</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {(selected || detailsLoading) && (
        <div className="ag-overlay">
          <div className="ag-modal">
            {detailsLoading ? (
              <div className="ag-empty">Chargement du dossier…</div>
            ) : (
              <>
                <button
                  className="ag-close"
                  onClick={() => setSelected(null)}
                >
                  ×
                </button>

                <div className="ag-eyebrow">
                  AGENCE #{selected.agency.id}
                </div>

                <div className="ag-hero">
                  <div className="ag-big-logo">
                    {selected.agency.logo_url ? (
                      <img
                        src={mediaUrl(selected.agency.logo_url)}
                        alt=""
                      />
                    ) : (
                      String(selected.agency.name || "A")
                        .slice(0, 1)
                    )}
                  </div>
                  <div>
                    <h2>{selected.agency.name}</h2>
                    <div className="ag-status">
                      {LABELS[selected.agency.status] ||
                        selected.agency.status}
                    </div>
                    <div className="ag-muted">
                      {selected.agency.description}
                    </div>
                  </div>
                </div>

                <div className="ag-info">
                  <div className="ag-box">
                    <span className="ag-muted">Président</span>
                    <strong>{selected.agency.president_pseudo}</strong>
                  </div>
                  <div className="ag-box">
                    <span className="ag-muted">Compte</span>
                    <strong>{selected.agency.president_status}</strong>
                  </div>
                  <div className="ag-box">
                    <span className="ag-muted">Pack actif/demandé</span>
                    <strong>{selected.agency.bonus_level || requestedLevel} %</strong>
                  </div>
                  <div className="ag-box">
                    <span className="ag-muted">Capacité</span>
                    <strong>{selected.agency.member_limit} membres</strong>
                  </div>
                </div>

                {[
                  ["Objectifs", selected.agency.objectives],
                  ["Critères de recrutement", selected.agency.recruitment_criteria],
                  ["Expérience du président", selected.agency.president_experience]
                ].map(item => (
                  <React.Fragment key={item[0]}>
                    <h3 className="ag-section">{item[0]}</h3>
                    <div className="ag-detail">
                      {item[1] || "Non renseigné"}
                    </div>
                  </React.Fragment>
                ))}

                <h3 className="ag-section">Spécialités</h3>
                <div className="ag-tags">
                  {parseList(selected.agency.specialties_json).map(item => (
                    <span className="ag-tag" key={item}>{item}</span>
                  ))}
                </div>

                <h3 className="ag-section">Services</h3>
                <div className="ag-tags">
                  {parseList(selected.agency.services_json).map(item => (
                    <span className="ag-tag" key={item}>{item}</span>
                  ))}
                </div>

                <h3 className="ag-section">Message ou motif Admin</h3>
                <textarea
                  className="ag-textarea"
                  value={reason}
                  onChange={event => setReason(event.target.value)}
                  placeholder="Explique clairement l’avertissement ou la décision…"
                />

                <div className="ag-actions">
                  <button
                    disabled={processing}
                    className="ag-action warn"
                    onClick={() => control("warn")}
                  >
                    Avertir
                  </button>

                  {selected.agency.status === "suspended" ? (
                    <button
                      disabled={processing}
                      className="ag-action reactivate"
                      onClick={() => control("reactivate")}
                    >
                      Réactiver
                    </button>
                  ) : (
                    <button
                      disabled={processing}
                      className="ag-action suspend"
                      onClick={() => control("suspend")}
                    >
                      Suspendre
                    </button>
                  )}

                  <button
                    disabled={processing}
                    className="ag-action revoke"
                    onClick={() => control("revoke")}
                  >
                    Retirer la certification
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminAgenciesPage;
